import { styled } from "@linaria/react";
import React from "react";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import type { IndexedFailureValues } from "../../../../../api/useIndexedFailureValues";
import type { Project } from "../../../../../api/useProjects";
import type { Setup } from "../../../../../api/useSetups";
import HighchartsComponent from "@ui/Highcharts";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import useSeries from "../useSeries";
import type { ObservableOrder } from "./useOptions";
import useOptions from "./useOptions";
import type { IndexedManualFailureValues } from "../../../../../api/useManualFailureValues";
import type { IndexedFailureMetadata } from "../../../../../api/useFailureMetadata";
import { useFlagSelectedQuantities } from "../hook/useCommonOptions";
import type { Quantity } from "../../../../../api/useQuantities";

interface P {
  visibleSymptomHistogramChartId: string;
  project: Project;
  setups: Setup[];
  analysis: ActiveAnalysis;
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
  order: ObservableOrder;
  observableChartRef: React.RefObject<Highcharts.Chart>;
  quantities: Quantity[];
  showInactiveObservables: boolean;
}

const FixedWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const Wrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  height: initial !important; /* remove Higcharts style override... */
`;

const ObservablesChart = ({
  visibleSymptomHistogramChartId,
  project,
  setups,
  analysis,
  indexedFailureMetadata,
  indexedFailureValues,
  indexedManualFailureValues,
  order,
  observableChartRef,
  quantities,
  showInactiveObservables,
}: P) => {
  const [flagSelectedQuantities] = useFlagSelectedQuantities({ scope: project.id });

  const setup = getAnalysisSetup(setups, analysis);
  const inputs = React.useMemo(() => (setup ? setup.diagnosisInputs : []), [setup]);
  const observables = React.useMemo(
    () =>
      inputs
        .filter(input =>
          showInactiveObservables
            ? quantities.some(quantity => quantity.id === input.quantityId)
            : quantities.some(quantity => quantity.id === input.quantityId) && input.enabled,
        )
        .flatMap(input => {
          const quantityFound = quantities.find(q => input.quantityId === q.id);
          if (quantityFound) {
            return [
              {
                ...input,
                ...quantityFound,
              },
            ];
          }

          return [];
        }),
    [inputs, quantities, showInactiveObservables],
  );

  const series = useSeries(
    project,
    analysis,
    indexedFailureMetadata,
    indexedFailureValues,
    indexedManualFailureValues,
    setup,
    observables,
    true,
  );
  const options = useOptions({
    visibleSymptomHistogramChartId,
    series,
    analysis,
    order,
    quantities,
    setups,
    project,
    showInactiveObservables,
    flagSelectedQuantities,
    observables,
  });
  return (
    <FixedWrapper>
      <Wrapper>
        <HighchartsComponent
          ref={observableChartRef}
          name="Diagnosis impacts"
          options={options}
          selectablePoints={false}
        />
      </Wrapper>
    </FixedWrapper>
  );
};

export default React.memo(ObservablesChart);
